import React, { createRef } from 'react';
import { Navigate } from 'react-router-dom';
import Route from 'enum/route.enum';
import MenuId from 'enum/menu-id.enum';
import CachedImg from 'components/common/CachedImg/CachedImg';
import { trackPageItem } from 'components/main/common/utils';

// Styled Components
import {
  Container,
  HiddenPlayerMount,
  LiveBadge,
  RadioCardLogoBox,
  RadioIcon,
  SignalIcon,
  StationCard,
  StationFrequency,
  StationTitle,
} from './styles';
import HeaderBar from './components/HeaderBar';
import {
  ChannelListArea,
  ChannelListHeader,
  ChannelListTitle,
  ChannelCount,
  ChannelListContainer,
  ChannelItem,
  ChannelLogo,
  ChannelInfo,
  ChannelName,
  VideoPlayerArea,
  RadioPreviewPane,
  RadioNowPlayingStage,
  TVChannelsOuterRow,
  TVChannelsLeftGroup,
  CategoryPanelArea,
  CategoryPanelHeader,
  CategoryListContainer,
  CategoryItem,
  ChannelLoadingBackdrop,
  ChannelLoadingCard,
  SpinnerGlow,
  SpinnerArc,
  SpinnerDot,
  ChannelLoadingName,
  LoadingDotsRow,
  LoadingDot,
  ChannelUnavailableStatus,
  ChannelUnavailableMessage,
  FullscreenChannelNumber,
} from './components/TVChannelsList/styles';

import BaseChannelTemplate from 'components/channels/BaseChannelTemplate';
import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import { updatePlayerState } from 'components/channels/common/channelPlayerState';

import { Container as CommonContainer } from 'components/styles/Common.styled';
import { measureComponent } from 'utils/common/measureComponent';
import { scrollChildIntoView, scrollIntoView } from 'utils/dom';

import { translate as t } from 'utils/i18n';
import { getPlayerState } from 'components/channels/common/channelPlayerState';
import { ContentContainer } from 'design-tokens/warwick/common';
import PageHeader from './components/PageHeader';

/**
 * WarwickV1 Live TV and Radio — three-column layout:
 *   Left: Category panel  |  Middle: Channel/station list  |  Right: Video or now-playing
 * Pass isRadio={true} for the radio screen.
 */

const ALL_CATEGORIES_LABEL = 'All';

const SECTION = {
  CATEGORY_LIST: 'categoryList',
  CHANNEL_LIST: 'channelList',
  BACK_BUTTON: 'backButton',
};

class TVChannelsList extends BaseChannelTemplate {
  static defaultProps = {
    isRadio: false,
  };

  constructor(props) {
    super(props);
    this.backButtonRef = createRef();
    this.activeChannelRef = createRef();
    this.activeCategoryRef = createRef();
    this.controlsHideTimer = null;
    /** True after Enter watch mode until user returns to the 3-column layout. */
    this._tvInWatchMode = false;

    this.state = Object.assign({}, this.state, {
      slideList: true,
      focusedSection: SECTION.CHANNEL_LIST,
      // Category state
      categories: [],
      selectedCategory: ALL_CATEGORIES_LABEL,
      categoryFocusIndex: 0,
      categoryDisplayList: [],
      // Subset of items matching the active category — navigation always uses this
      filteredItems: [],
    });
  }

  isRadioMode() {
    return Boolean(this.props.isRadio);
  }

  trackChannelItem() {
    const { items, focusIndex } = this.state;
    if (!items || !items[focusIndex]) {
      return;
    }
    const menuId = this.isRadioMode() ? MenuId.RADIO : MenuId.CHANNELS;
    trackPageItem(menuId, items[focusIndex]?.channel_id?.locale_english?.locale_name);
  }

  renderErrorStates() {
    if (!this.isRadioMode()) {
      return super.renderErrorStates();
    }

    const { loading, showError, items } = this.state;
    return (
      <>
        {items.length <= 0 && !showError && !loading && (
          <div className={`has-text-danger has-text-centered`}>
            <p className={'is-size-4 has-text-bold'}>{t('Radio.radio_error')}</p>
          </div>
        )}
        {items.length <= 0 && showError && !loading && (
          <div className={`has-text-danger has-text-centered`}>
            <p className={'is-size-4 has-text-bold'}>
              The selected language is not configured yet.
            </p>
          </div>
        )}
      </>
    );
  }

  componentWillUnmount() {
    clearTimeout(this.controlsHideTimer);
    this._tvInWatchMode = false;
    super.componentWillUnmount();
  }

  /**
   * TV watch / fullscreen UI (lists hidden or native fullscreen). Uses an instance
   * flag so Back/Left/Right still exit when slideList briefly flips true (e.g. after
   * Up/Down) or when signal-unavailable leaves displayFullScreen false on Tizen.
   */
  isTvWatchMode() {
    if (this.isRadioMode()) {
      return false;
    }
    return (
      this._tvInWatchMode ||
      !this.state.slideList ||
      getPlayerState().displayFullScreen
    );
  }

  componentDidMount() {
    super.componentDidMount();
    var deviceList = Storages.MainStorage.getItem(StorageKeys.Keys.DEVICELIST);
    if (this.isRadioMode()) {
      document.documentElement.classList.remove('tep-preview-active');
      document.body.classList.remove('tep-preview-active');
      updatePlayerState({ displayFullScreen: true });
    } else if (deviceList && deviceList.isTIZEN_TEP) {
      document.documentElement.classList.remove('tep-preview-active');
      document.body.classList.remove('tep-preview-active');
      document.documentElement.classList.add('tep-preview-pending');
      document.body.classList.add('tep-preview-pending');
      updatePlayerState({ displayFullScreen: false });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate(prevProps, prevState);

    if (prevState.focusIndex !== this.state.focusIndex) {
      this.scrollActiveChannelIntoView();
    }

    if (
      prevState.categoryFocusIndex !== this.state.categoryFocusIndex ||
      (prevState.focusedSection !== this.state.focusedSection &&
        this.state.focusedSection === SECTION.CATEGORY_LIST)
    ) {
      this.scrollActiveCategoryIntoView();
    }

    // If items changed and we have items but no filteredItems, initialize them
    if (prevState.items !== this.state.items && this.state.items && this.state.items.length > 0) {
      if (!this.state.filteredItems || this.state.filteredItems.length === 0) {
        console.log('componentDidUpdate: Re-initializing filteredItems and categories');
        this.setState(
          {
            filteredItems: this.state.items,
            selectedCategory: ALL_CATEGORIES_LABEL,
          },
          () => {
            this.deriveCategories();
          }
        );
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Data loading — extend base to derive categories after channels are loaded
  // ---------------------------------------------------------------------------

  /**
   * Load channels and start playback. Must set filteredItems in the SAME setState
   * as items before checkDevice() — super.getData() alone leaves filteredItems []
   * so the first channel URL is missing (playback only works from 2nd channel).
   */
  async getData() {
    try {
      var focusIndex = this.state.focusIndex;
      this.setState({ loading: true });

      var orgChannels = this.channelData.getOrgChannels();

      if (!orgChannels.length) {
        await this.stopPlayer();
        this.setState({
          showError: false,
          loading: false,
          items: [],
          channelsList: [],
          filteredItems: [],
          categories: [ALL_CATEGORIES_LABEL],
          categoryDisplayList: [{ label: ALL_CATEGORIES_LABEL, active: true }],
          selectedCategory: ALL_CATEGORIES_LABEL,
        });
        return;
      }

      var processedChannels = this.channelData.processChannelsWithLocales(orgChannels);
      var modeChannels = this.channelData.filterChannelsByMode(
        processedChannels,
        this.isRadioMode()
      );
      var showError = modeChannels.length === 0;
      if (focusIndex >= modeChannels.length) {
        focusIndex = 0;
      }
      var channelsList = await this.generateNewChannelList(focusIndex, modeChannels);

      this.setState(
        {
          items: modeChannels,
          channelsList: channelsList,
          filteredItems: modeChannels,
          selectedCategory: ALL_CATEGORIES_LABEL,
          showError: showError,
          loading: false,
        },
        () => {
          this.deriveCategories();
          this.checkDevice(focusIndex);
        }
      );
    } catch (error) {
      this.setState({
        items: [],
        filteredItems: [],
        loading: false,
      });
    }
  }

  /**
   * Extract unique channel_type values from loaded items, prepend "All",
   * and build the display list to show all categories.
   */
  async deriveCategories() {
    const { items } = this.state;

    // Double-check items exist
    if (!items || items.length === 0) {
      console.log('deriveCategories: No items, setting default category');
      this.setState({
        categories: [ALL_CATEGORIES_LABEL],
        categoryDisplayList: [{ label: ALL_CATEGORIES_LABEL, active: true }],
        categoryFocusIndex: 0,
      });
      return;
    }

    console.log('deriveCategories: Deriving from', items.length, 'items');

    var types = items
      .map(function (ch) {
        return ch.channel_type;
      })
      .filter(function (v) {
        return Boolean(v);
      });

    var unique = types.filter(function (value, index, self) {
      return self.indexOf(value) === index;
    });

    var categories = [ALL_CATEGORIES_LABEL].concat(unique);

    console.log('deriveCategories: Found categories:', categories);

    // Build display list with all categories visible
    const categoryDisplayList = this.buildCategoryDisplayList(0, categories, categories.length);

    console.log(
      'deriveCategories: Setting state with',
      categoryDisplayList.length,
      'category display items'
    );

    this.setState(
      {
        categories,
        categoryDisplayList,
        categoryFocusIndex: 0,
      },
      () => {
        console.log('deriveCategories: State updated. Current categories:', this.state.categories);
        console.log(
          'deriveCategories: Current categoryDisplayList:',
          this.state.categoryDisplayList
        );
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Measurement helpers
  // ---------------------------------------------------------------------------

  getMeasurements(sampleItem) {
    return measureComponent(() => this.renderChannelItem(sampleItem, 0), {
      useableDimension: 0.6,
      width: false,
      ensureOdd: false,
      extraSpace: 16,
    });
  }

  // ---------------------------------------------------------------------------
  // Channel list generation (unchanged logic from original)
  // ---------------------------------------------------------------------------

  async generateNewChannelList(newIndex, list) {
    if (!list || list.length === 0) return [];
    if (newIndex < 0 || newIndex >= list.length) newIndex = 0;
    return list.map((item, index) =>
      index === newIndex ? Object.assign({}, item, { active: true }) : item
    );
  }

  // ---------------------------------------------------------------------------
  // Category list windowing - Show all categories, not windowed
  // ---------------------------------------------------------------------------

  buildCategoryDisplayList(newIndex, list, allowedCategories) {
    if (!list || list.length === 0) return [];

    // Show ALL categories, not just a window
    // Mark the active one based on newIndex
    return list.map((label, index) => ({
      label: label,
      active: index === newIndex,
    }));
  }

  async changeCategoryIndex(newIndex) {
    const { categories } = this.state;
    if (!categories || categories.length === 0) return;

    // Wrap around
    newIndex = ((newIndex % categories.length) + categories.length) % categories.length;

    const categoryDisplayList = this.buildCategoryDisplayList(
      newIndex,
      categories,
      categories.length
    );
    this.setState({ categoryFocusIndex: newIndex, categoryDisplayList });
  }

  async selectCategory(label) {
    const { items, selectedCategory } = this.state;

    // Skip if already on this category
    if (label === selectedCategory) {
      this.setState({ focusedSection: SECTION.CHANNEL_LIST });
      return;
    }

    const filtered = this.getFilteredItems(items, label);

    // Commit category state first; once it's applied, delegate channel navigation
    // entirely to changeChannelIndex so there's a single code path for that logic.
    // change focusIndex to -1 otherwise on select category if old focus index is 0
    // new would also not work
    this.setState(
      {
        selectedCategory: label,
        filteredItems: filtered,
        focusedSection: SECTION.CHANNEL_LIST,
        focusIndex: -1,
      },
      () => {
        // Only change channel if we have filtered items
        if (filtered && filtered.length > 0) {
          this.changeChannelIndex(0);
        }
      }
    );
  }

  /**
   * Returns items filtered by selectedCategory, or all items when "All" is selected.
   */
  getFilteredItems(items, category = this.state.selectedCategory) {
    if (!category || category === ALL_CATEGORIES_LABEL) return items;
    return items.filter((ch) => ch.channel_type === category);
  }

  // ---------------------------------------------------------------------------
  // Scope all navigation and playback to the active filtered subset
  // ---------------------------------------------------------------------------

  getActiveItems() {
    var filtered = this.state.filteredItems;
    if (filtered && filtered.length > 0) {
      return filtered;
    }
    return this.state.items;
  }

  // ---------------------------------------------------------------------------
  // Scroll helpers
  // ---------------------------------------------------------------------------

  /**
   * Scroll the active channel item into view within ChannelListContainer.
   * Uses manual scrollTop arithmetic instead of scrollIntoView({ block:'nearest' })
   * because old Tizen WebKit only supports the boolean overload of scrollIntoView.
   */
  scrollActiveChannelIntoView() {
    scrollChildIntoView(this.activeChannelRef.current, null, 'vertical');
  }

  /**
   * Scroll the active (focused) category item into view within CategoryListContainer.
   */
  scrollActiveCategoryIntoView() {
    scrollChildIntoView(this.activeCategoryRef.current, null, 'vertical');
  }

  // ---------------------------------------------------------------------------
  // Keyboard navigation
  // ---------------------------------------------------------------------------

  /**
   * Section toggle helpers — kept small so getKeyPressCallbacks stays readable.
   */
  bringBackControls() {
    clearTimeout(this.controlsHideTimer);
    if (!this.state.slideList) {
      this.setState({ slideList: true });
    }
    var self = this;
    this.controlsHideTimer = setTimeout(function () {
      if (!self._isMounted) {
        return;
      }
      if (getPlayerState().displayFullScreen || self.isRadioMode()) {
        self.setState({ slideList: false });
      }
    }, this.sleepTime);
  }

  enterWatchMode() {
    if (!this.isRadioMode()) {
      this._tvInWatchMode = true;
    }
    this.setState({ slideList: false });
    if (!this.isRadioMode()) {
      var playerState = getPlayerState();
      if (!playerState.displayFullScreen) {
        this.screenResolution();
      }
    }
  }

  /**
   * TV fullscreen → WarwickV1 3-column preview. Restores slideList before the player
   * re-measures VideoPlayerArea (toggleFullscreen uses preview rect bounds).
   */
  async exitTvFullscreenToThreeColumn() {
    if (this.isRadioMode()) {
      return;
    }

    this._tvInWatchMode = false;
    clearTimeout(this.controlsHideTimer);
    this.setState({ slideList: true });

    var playerState = getPlayerState();
    if (!playerState.displayFullScreen) {
      return;
    }

    if (this.player.supportsFullscreenToggle()) {
      await new Promise(function (resolve) {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(function () {
            requestAnimationFrame(resolve);
          });
        } else {
          setTimeout(resolve, 48);
        }
      });
      await this.player.toggleFullscreen();
    } else {
      updatePlayerState({ displayFullScreen: false });
    }
  }

  slideShowList() {
    // List visibility is managed by slideList + bringBackControls / enterWatchMode only.
    // Base changeChannelIndex calls slideShowList and would undo an immediate hide on Enter.
  }

  async checkResolution(redirectBack) {
    if (!redirectBack) return;

    // Radio watch mode: Back restores the 3-column layout (do not exit the app).
    if (this.isRadioMode()) {
      if (!this.state.slideList) {
        this.setState({ slideList: true });
        clearTimeout(this.controlsHideTimer);
        return;
      }
      await this.stopPlayer();
      this.setState({ redirectBack: true });
      return;
    }

    // TV: Back from watch/fullscreen returns to the 3-column layout (not the landing page).
    if (this.isTvWatchMode()) {
      await this.exitTvFullscreenToThreeColumn();
      return;
    }

    return super.checkResolution(redirectBack);
  }

  focusSection(section) {
    const leavingCategoryPanel =
      this.state.focusedSection === SECTION.CATEGORY_LIST && section !== SECTION.CATEGORY_LIST;

    if (leavingCategoryPanel) {
      // Restore the category panel highlight to match what channels are actually showing,
      // discarding any browsing the user did without pressing Enter.
      const { categories, selectedCategory } = this.state;
      const selectedIndex = Math.max(0, categories.indexOf(selectedCategory));
      const categoryDisplayList = this.buildCategoryDisplayList(
        selectedIndex,
        categories,
        categories.length
      );
      this.setState({
        focusedSection: section,
        categoryFocusIndex: selectedIndex,
        categoryDisplayList,
      });
    } else {
      this.setState({ focusedSection: section });
    }

    if (section === SECTION.BACK_BUTTON) {
      this.backButtonRef.current?.focus();
    } else {
      this.backButtonRef.current?.blur();
    }
  }

  getKeyPressCallbacks() {
    var base = super.getKeyPressCallbacks();
    var self = this;

    var callbacks = Object.assign({}, base);

    callbacks.onVerticalMove = function (delta) {
      self.bringBackControls();
      var focusedSection = self.state.focusedSection;
      var focusIndex = self.state.focusIndex;
      var categoryFocusIndex = self.state.categoryFocusIndex;
      var filteredItems = self.state.filteredItems;

      console.log('onVerticalMove: delta =', delta, 'focusedSection =', focusedSection);
      console.log(
        'onVerticalMove: focusIndex =',
        focusIndex,
        'filteredItems length =',
        filteredItems ? filteredItems.length : 0
      );

      if (focusedSection === SECTION.CHANNEL_LIST) {
        if (!filteredItems || filteredItems.length === 0) {
          console.log('onVerticalMove: No filteredItems, skipping navigation');
          return;
        }
        self.changeChannelIndex(focusIndex + delta);
      } else if (focusedSection === SECTION.CATEGORY_LIST) {
        self.changeCategoryIndex(categoryFocusIndex + delta);
      }
    };

    callbacks.onHorizontalMove = function (delta) {
      // Left / Right in TV watch/fullscreen exits to the 3-column layout (not a brief overlay).
      if (self.isTvWatchMode()) {
        self.exitTvFullscreenToThreeColumn();
        return;
      }

      self.bringBackControls();
      var focusedSection = self.state.focusedSection;
      var channelOnly = self.state.channelOnly;

      if (focusedSection === SECTION.CHANNEL_LIST) {
        if (delta < 0) {
          self.focusSection(SECTION.CATEGORY_LIST);
        } else if (delta > 0 && !channelOnly) {
          self.focusSection(SECTION.BACK_BUTTON);
        }
      } else if (focusedSection === SECTION.CATEGORY_LIST) {
        if (delta > 0) self.focusSection(SECTION.CHANNEL_LIST);
      } else if (focusedSection === SECTION.BACK_BUTTON) {
        if (delta < 0) self.focusSection(SECTION.CHANNEL_LIST);
      }
    };

    callbacks.onBack = function (channelOnly) {
      if (self.isTvWatchMode()) {
        self.exitTvFullscreenToThreeColumn();
        return;
      }
      return base.onBack(channelOnly);
    };

    callbacks.onEnter = function () {
      var focusedSection = self.state.focusedSection;
      var categoryFocusIndex = self.state.categoryFocusIndex;
      var categories = self.state.categories;

      if (focusedSection === SECTION.BACK_BUTTON) {
        self.setState({ redirectBack: true });
      } else if (focusedSection === SECTION.CATEGORY_LIST) {
        self.selectCategory(categories[categoryFocusIndex]);
      } else {
        self.enterWatchMode();
      }
    };

    return callbacks;
  }
  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  /**
   * Invisible sample used only for measuring category item height.
   * Must match the real CategoryItem padding/font so the measurement is accurate.
   */
  renderCategoryItemSample(label) {
    return <CategoryItem>{label}</CategoryItem>;
  }

  renderChannelItem(channel) {
    const locale_name = channel?.channel_id?.locale_channel?.locale_name || 'Unknown Channel';
    const image = channel?.channel_id?.channel?.image_url;
    const sectionFocused = this.state.focusedSection === SECTION.CHANNEL_LIST;
    const isRadio = this.isRadioMode();

    return (
      <ChannelItem
        key={`channel-${channel.id}`}
        ref={channel.active ? this.activeChannelRef : null}
        active={channel.active}
        sectionFocused={sectionFocused}
        onClick={() => {
          const index = this.state.filteredItems.findIndex((item) => item.id === channel.id);
          this.changeChannelIndex(index);
        }}
        tabIndex={0}
      >
        {isRadio ? (
          <RadioCardLogoBox>
            <RadioIcon variant='outline' width={28} height={28} />
          </RadioCardLogoBox>
        ) : (
          <ChannelLogo>
            {image && <CachedImg src={image} width='48px' height='48px' alt={locale_name} />}
          </ChannelLogo>
        )}
        <ChannelInfo>
          <ChannelName>{locale_name}</ChannelName>
        </ChannelInfo>
      </ChannelItem>
    );
  }

  renderCategoryList() {
    const { categoryDisplayList, focusedSection, categories } = this.state;

    console.log('renderCategoryList: categories =', categories);
    console.log('renderCategoryList: categoryDisplayList =', categoryDisplayList);

    // Don't render if categories aren't ready
    if (
      !categories ||
      categories.length === 0 ||
      !categoryDisplayList ||
      categoryDisplayList.length === 0
    ) {
      console.log('renderCategoryList: Skipping render - categories not ready');
      return null;
    }

    const sectionFocused = focusedSection === SECTION.CATEGORY_LIST;

    return (
      <CategoryPanelArea>
        <CategoryPanelHeader>{t('Channels.categories')}</CategoryPanelHeader>
        <CategoryListContainer>
          {categoryDisplayList.map((cat, index) => (
            <CategoryItem
              key={`category-${cat.label}-${index}`}
              ref={cat.active ? this.activeCategoryRef : null}
              active={cat.active}
              sectionFocused={sectionFocused}
              onClick={() => {
                const realIndex = this.state.categories.indexOf(cat.label);
                if (realIndex !== -1) {
                  this.changeCategoryIndex(realIndex);
                  this.selectCategory(cat.label);
                }
              }}
            >
              {cat.label}
            </CategoryItem>
          ))}
        </CategoryListContainer>
      </CategoryPanelArea>
    );
  }

  getRadioSignalBars(count = 30) {
    const bars = [];
    for (let i = 0; i < count; i++) {
      bars.push(<span key={i} />);
    }
    return bars;
  }

  renderChannelList(channels) {
    const isRadio = this.isRadioMode();
    const itemCount = this.getFilteredItems(this.state.items).length;

    return (
      <ChannelListArea>
        <ChannelListHeader>
          <ChannelListTitle>{isRadio ? t('Radio.radio') : t('Channels.live_tv')}</ChannelListTitle>
          <ChannelCount>
            {(isRadio ? t('Radio.radio_channels_count') : t('Channels.tv_channels_count')).replace(
              '{count}',
              itemCount
            )}
          </ChannelCount>
        </ChannelListHeader>

        <ChannelListContainer>
          {channels.map((channel) => this.renderChannelItem(channel))}
        </ChannelListContainer>
      </ChannelListArea>
    );
  }

  renderRadioPlayer() {
    const { filteredItems, focusIndex, channelOnly, channelLoading, channelSignalUnavailable, slideList } =
      this.state;
    const activeChannel = filteredItems?.[focusIndex];
    const title = activeChannel?.channel_id?.locale_channel?.locale_name ?? '';
    const frequency = activeChannel?.channel_id?.locale_channel?.locale_description ?? '';
    const showNowPlaying = !channelLoading && !channelSignalUnavailable;
    const bars = this.getRadioSignalBars(30);
    const nowPlayingContent = showNowPlaying ? (
      <>
        <StationCard>
          <RadioIcon variant='outline' width={90} height={90} />
        </StationCard>
        <LiveBadge isActive={Boolean(activeChannel)}>{t('Radio.live')}</LiveBadge>
        <StationTitle>{title}</StationTitle>
        <StationFrequency>{frequency || t('Radio.playing_now')}</StationFrequency>
        {activeChannel && (
          <SignalIcon height='40px' barCount={bars.length} width='200px'>
            {bars}
          </SignalIcon>
        )}
      </>
    ) : null;

    // Watch mode (lists hidden): fullscreen black stage, no column Back button (use remote Back).
    if (!slideList) {
      return (
        <RadioNowPlayingStage>
          <RadioPreviewPane>{nowPlayingContent}</RadioPreviewPane>
          <HiddenPlayerMount>{super.getPlayerContainers()}</HiddenPlayerMount>
          {this.renderChannelLoadingOverlay(true)}
        </RadioNowPlayingStage>
      );
    }

    // 3-column layout: radio UI and loading anchored to the 3rd-column VideoPlayerArea.
    return (
      <ContentContainer data-tizen-content-container>
        <PageHeader
          title=''
          ref={this.backButtonRef}
          onBack={channelOnly ? undefined : () => this.setState({ redirectBack: true })}
          backFocused={
            !channelOnly && this.state.focusedSection === SECTION.BACK_BUTTON
          }
        />
        <VideoPlayerArea>
          <RadioPreviewPane>{nowPlayingContent}</RadioPreviewPane>
          <HiddenPlayerMount>{super.getPlayerContainers()}</HiddenPlayerMount>
          {this.renderChannelLoadingOverlay(false)}
        </VideoPlayerArea>
      </ContentContainer>
    );
  }

  renderVideoPlayer() {
    const playerState = getPlayerState();
    const isFullscreen = playerState.displayFullScreen;

    return (
      <ContentContainer data-tizen-content-container>
        <PageHeader
          title=''
          ref={this.backButtonRef}
          onBack={this.state.channelOnly ? undefined : () => this.setState({ redirectBack: true })}
          backFocused={
            !this.state.channelOnly && this.state.focusedSection === SECTION.BACK_BUTTON
          }
        />
        <VideoPlayerArea>
          {this.getPlayerContainers()}
          {/* In preview mode, render loading overlay inside VideoPlayerArea */}
          {!isFullscreen && this.renderChannelLoadingOverlay(false)}
        </VideoPlayerArea>
      </ContentContainer>
    );
  }

  // ---------------------------------------------------------------------------
  // Channel loading overlay
  // ---------------------------------------------------------------------------

  renderChannelLoadingOverlay(isFullscreen = false) {
    const { channelLoading, channelSignalUnavailable, filteredItems, focusIndex } = this.state;
    if (!channelLoading && !channelSignalUnavailable) return null;

    const channelName = filteredItems?.[focusIndex]?.channel_id?.locale_channel?.locale_name ?? '';
    // 1-based OSD number — same convention guests expect from live TV / remote entry
    const channelNumber = focusIndex >= 0 ? focusIndex + 1 : null;

    return (
      <ChannelLoadingBackdrop $fullscreen={isFullscreen}>
        {isFullscreen && channelNumber != null && (
          <FullscreenChannelNumber>{channelNumber}</FullscreenChannelNumber>
        )}
        <ChannelLoadingCard>
          {channelSignalUnavailable ? (
            <>
              <ChannelUnavailableStatus>
                <ChannelUnavailableMessage>
                  {t('Channels.signal_unavailable')}
                </ChannelUnavailableMessage>
              </ChannelUnavailableStatus>
              <ChannelLoadingName>{channelName}</ChannelLoadingName>
            </>
          ) : (
            <>
              <SpinnerGlow>
                <SpinnerArc>
                  <SpinnerDot />
                </SpinnerArc>
              </SpinnerGlow>
              <ChannelLoadingName>{channelName}</ChannelLoadingName>
              <LoadingDotsRow>
                <LoadingDot delay='0s' />
                <LoadingDot delay='0.2s' />
                <LoadingDot delay='0.4s' />
              </LoadingDotsRow>
            </>
          )}
        </ChannelLoadingCard>
      </ChannelLoadingBackdrop>
    );
  }

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  render() {
    if (this.state.redirectBack) {
      return <Navigate to={Route.route.LANDING} />;
    }

    const { loading, showError, channelsList, slideList } = this.state;
    const playerState = getPlayerState();
    const isFullscreen = playerState.displayFullScreen;
    const showChannelLists =
      slideList && channelsList.length > 0 && !showError && !loading;
    const isRadioWatchMode =
      this.isRadioMode() && !showChannelLists && channelsList.length > 0 && !showError && !loading;

    return (
      <Container
        data-tizen-page-container
        style={isRadioWatchMode ? { backgroundColor: '#000000' } : undefined}
      >
        <HeaderBar />
        <TVChannelsOuterRow
          style={isRadioWatchMode ? { backgroundColor: '#000000' } : undefined}
        >
          {channelsList.length < 1 && <CommonContainer>{this.renderErrorStates()}</CommonContainer>}
          {channelsList.length > 0 && !showError && !loading && (
            <>
              {showChannelLists && (
                <TVChannelsLeftGroup>
                  {this.renderCategoryList()}
                  {this.renderChannelList(channelsList)}
                </TVChannelsLeftGroup>
              )}
              {this.isRadioMode() ? this.renderRadioPlayer() : this.renderVideoPlayer()}
            </>
          )}
        </TVChannelsOuterRow>
        {/* Fullscreen TV: root overlay matches native setDisplayRect (full viewport). */}
        {!this.isRadioMode() && isFullscreen && this.renderChannelLoadingOverlay(true)}
      </Container>
    );
  }
}

export default TVChannelsList;
